import { useEffect, useMemo, useRef, useState } from 'react';
import { meApi, ventesApi } from '../api/endpoints';
import type { MeStatsDto, Period, UserDto, VenteDto } from '../api/types';
import { useToast } from '../hooks/useToast';
import { fireConfetti } from '../lib/confetti';
import { downloadCSV, exportPDF, isImmo, shareRecap } from '../lib/exports';
import { fmtDate, fmtEur, fmtK, todayIso } from '../lib/format';
import { computeSalary } from '../lib/salary';
import { TargetModal } from '../components/TargetModal';
import { Link } from 'react-router-dom';

const PERIODS: { code: Period; label: string }[] = [
  { code: 'week',  label: 'Semaine' },
  { code: 'month', label: 'Ce mois' },
  { code: 'year',  label: 'Année' },
  { code: 'all',   label: 'Tout' }
];

const PERIOD_LABELS: Record<Period, string> = {
  week:  'Cette semaine',
  month: 'Ce mois',
  year:  'Cette année',
  all:   'Toutes mes ventes'
};

export function MesVentesPage() {
  const toast = useToast();
  const [period, setPeriod] = useState<Period>('month');
  const [search, setSearch] = useState('');
  const [ventes, setVentes] = useState<VenteDto[]>([]);
  const [stats, setStats] = useState<MeStatsDto>();
  const [user, setUser] = useState<UserDto>();
  const [loading, setLoading] = useState(true);
  const [showTargetModal, setShowTargetModal] = useState(false);

  // Détection franchissement objectif → confettis (1 fois par mois)
  const prevProgressPctRef = useRef<number | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const [list, me] = await Promise.all([
        ventesApi.list(period, search.trim() || undefined),
        meApi.get()
      ]);
      setVentes(list);
      setStats(me.stats);
      setUser(me.user);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Chargement impossible', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Confettis quand on franchit 100 % de l'objectif (par session)
  useEffect(() => {
    if (!stats || !user) return;
    const prev = prevProgressPctRef.current;
    const cur = stats.progressPct;
    const target = stats.targetCom ?? 0;

    if (target > 0 && prev !== null && prev < 100 && cur >= 100) {
      const flagKey = `comeasy_celeb_${user.id}_${new Date().toISOString().slice(0, 7)}`;
      if (!localStorage.getItem(flagKey)) {
        fireConfetti({ origin: 'top', count: 120 });
        toast('🎉 Objectif mensuel atteint !', 'success');
        localStorage.setItem(flagKey, '1');
      }
    }
    prevProgressPctRef.current = cur;
  }, [stats, user, toast]);

  // Filtre local pour réactivité immédiate sur la recherche
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return ventes;
    return ventes.filter(v =>
      `${v.clientPrenom} ${v.clientNom} ${v.prodLabel} ${v.agence} ${v.notes ?? ''}`
        .toLowerCase().includes(s)
    );
  }, [ventes, search]);

  // KPIs calculés sur la liste filtrée
  const kpis = useMemo(() => {
    const collecte = filtered.filter(v => !isImmo(v)).reduce((s, v) => s + v.collecte, 0);
    const prixTTC  = filtered.filter(v =>  isImmo(v)).reduce((s, v) => s + v.montant,  0);
    const com      = filtered.reduce((s, v) => s + v.commission, 0);
    return { nb: filtered.length, collecte, prixTTC, com };
  }, [filtered]);

  // Salaire estimé : toujours sur le mois en cours, indépendant du filtre
  const salary = useMemo(() => {
    if (!user || !stats) return null;
    return computeSalary(stats.commissionMois, user.statut);
  }, [user, stats]);

  async function onShare() {
    const r = await shareRecap(filtered, PERIOD_LABELS[period]);
    if (r === 'copied')   toast('Récap copié dans le presse-papier', 'success');
    if (r === 'shared')   toast('Partagé', 'success');
  }

  async function onPDF() {
    try {
      await exportPDF(filtered, PERIOD_LABELS[period], user?.displayName ?? '');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Export PDF impossible', 'error');
    }
  }

  function onExcel() {
    downloadCSV(filtered, `comeasy_${period}_${todayIso()}.csv`);
  }

  async function onSaveTarget(value: number | null) {
    try {
      await meApi.update({ targetCom: value });
      setShowTargetModal(false);
      toast(value ? 'Objectif enregistré' : 'Objectif retiré', 'success');
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Enregistrement impossible', 'error');
    }
  }

  async function onDelete(v: VenteDto) {
    if (!confirm(`Supprimer la vente de ${v.clientPrenom} ${v.clientNom} ?`)) return;
    try {
      await ventesApi.delete(v.id);
      toast('Vente supprimée', 'success');
      refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Suppression impossible', 'error');
    }
  }

  return (
    <div className="py-4 space-y-4">
      {/* ─── Boutons d'action (Partager / PDF / Excel) ─── */}
      <div className="flex justify-end gap-2 flex-wrap">
        <ActionBtn onClick={onShare} icon="📤" label="Partager" />
        <ActionBtn onClick={onPDF}   icon="📄" label="PDF" />
        <ActionBtn onClick={onExcel} icon="📊" label="Excel" />
      </div>

      {/* ─── Onglets période ─── */}
      <nav className="grid grid-cols-4 gap-1 bg-surface-muted p-1 rounded-xl">
        {PERIODS.map(p => (
          <button
            key={p.code}
            onClick={() => setPeriod(p.code)}
            className={`py-2 text-xs font-demi rounded-lg transition-colors
              ${period === p.code
                ? 'bg-white text-ink shadow-sm'
                : 'text-ink-sub hover:text-ink'}`}
          >
            {p.label}
          </button>
        ))}
      </nav>

      {/* ─── Objectif mensuel ─── */}
      {stats && (
        <section>
          {stats.targetCom && stats.targetCom > 0 ? (
            <TargetCard
              current={stats.commissionMois}
              target={stats.targetCom}
              pct={stats.progressPct}
              onEdit={() => setShowTargetModal(true)}
            />
          ) : (
            <TargetEmpty onSet={() => setShowTargetModal(true)} />
          )}
        </section>
      )}

      {/* ─── Salaire estimé du mois ─── */}
      {salary && (
        <section className="flex items-center justify-between bg-teal-bg border border-teal/30 rounded-xl px-4 py-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-sub font-demi">
              Salaire estimé du mois
            </div>
            <div className="text-xs text-ink-sub mt-0.5">{salary.subline}</div>
          </div>
          <div className="text-2xl font-demi text-teal-dark whitespace-nowrap">
            {fmtEur(salary.total)}
          </div>
        </section>
      )}

      {/* ─── 4 KPI cards ─── */}
      <section className="grid grid-cols-4 gap-2">
        <Kpi label="Ventes"      value={String(kpis.nb)} color="text-ink" />
        <Kpi label="Collecte"    value={fmtK(kpis.collecte)} color="text-srx" />
        <Kpi label="Prix TTC"    value={fmtK(kpis.prixTTC)}  color="text-qts" />
        <Kpi label="Commissions" value={fmtK(kpis.com)}      color="text-teal-dark" />
      </section>

      {/* ─── Recherche ─── */}
      <input
        className="input"
        placeholder="🔎 Rechercher (client, agence, produit, notes…)"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {/* ─── Liste des ventes ─── */}
      {loading ? (
        <div className="text-center text-ink-sub py-8">Chargement…</div>
      ) : filtered.length === 0 ? (
        <EmptyState hasSearch={!!search} />
      ) : (
        <ul className="space-y-2">
          {filtered.map(v => <VenteRow key={v.id} v={v} onDelete={() => onDelete(v)} />)}
        </ul>
      )}

      {/* ─── Modal objectif ─── */}
      {showTargetModal && (
        <TargetModal
          current={stats?.targetCom ?? null}
          onSave={onSaveTarget}
          onClose={() => setShowTargetModal(false)}
        />
      )}
    </div>
  );
}

// ─── Sous-composants ────────────────────────────────────────────

function ActionBtn({ onClick, icon, label }: { onClick: () => void; icon: string; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-border bg-white hover:bg-surface-muted text-ink-sub hover:text-ink transition-colors"
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function Kpi({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="card text-center px-2 py-3.5 overflow-hidden">
      <div className="text-[9px] uppercase tracking-normal text-ink-sub font-demi mb-1.5 truncate">
        {label}
      </div>
      <div className={`text-base font-demi truncate ${color}`}>{value}</div>
    </div>
  );
}

function TargetCard({
  current, target, pct, onEdit
}: { current: number; target: number; pct: number; onEdit: () => void }) {
  const realPct = pct;
  const fillPct = Math.min(100, pct);

  // Formatage : 1 décimale, virgule française, pas de décimale si nombre rond
  const pctLabel = formatPct(realPct);

  let color = '#1a8a7d';
  let label = '🎯 Objectif du mois';
  if (realPct >= 100)      { color = '#C89010'; label = '🎉 Objectif atteint'; }
  else if (realPct >= 75)  { color = '#1a8a7d'; }
  else if (realPct >= 30)  { color = '#21b3a3'; }
  else                     { color = '#6B6F86'; }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-2">
        <div className="text-[11px] uppercase tracking-wider text-ink-sub font-demi">
          {label}
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-ink-sub hover:text-ink"
          aria-label="Modifier l'objectif"
        >
          ✏️ <span style={{ color, fontWeight: 600 }}>{pctLabel}</span>
        </button>
      </div>
      <div className="h-1.5 bg-surface-muted rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${fillPct}%`, background: color }}
        />
      </div>
      <div className="flex justify-between text-[11.5px] text-ink-sub mt-1.5">
        <span><strong className="text-ink font-demi">{fmtEur(current)}</strong></span>
        <span>{fmtEur(target)}</span>
      </div>
    </div>
  );
}

/** Formate un pourcentage à 1 décimale, virgule française. "12,5%" ou "100%". */
function formatPct(v: number): string {
  if (v >= 100) return `${Math.round(v)}%`;
  // 1 décimale, mais pas affichée si c'est un entier
  const rounded = Math.round(v * 10) / 10;
  if (Number.isInteger(rounded)) return `${rounded}%`;
  return `${rounded.toString().replace('.', ',')}%`;
}

function TargetEmpty({ onSet }: { onSet: () => void }) {
  return (
    <div className="card text-center py-5">
      <div className="text-sm text-ink-sub mb-2">Pas d'objectif mensuel défini</div>
      <button
        type="button"
        onClick={onSet}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal-bg border border-teal/30 text-teal-dark font-demi text-sm hover:bg-teal-bg/70 transition-colors"
      >
        🎯 Définir un objectif
      </button>
    </div>
  );
}

function VenteRow({ v, onDelete }: { v: VenteDto; onDelete: () => void }) {
  const subProd = [v.ppType, v.lodeomType, v.scpiMode, v.dispositif].filter(Boolean).join(' / ');
  const icon = pickIcon(v.prod);

  return (
    <li className="card flex gap-3 items-start hover:bg-surface-muted/30 transition-colors">
      <div className="w-9 h-9 shrink-0 rounded-lg bg-surface-muted flex items-center justify-center text-base">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-demi text-ink truncate">
            {v.clientPrenom} {v.clientNom}
          </span>
          <span className={`text-[9px] font-demi px-1.5 py-0.5 rounded ${
            v.entite === 'srx' ? 'bg-teal-bg text-teal-dark' : 'bg-qts-bg text-qts'
          }`}>
            {v.entite === 'srx' ? 'SRX' : 'QUIN'}
          </span>
          <span className="text-[10px] text-ink-dim ml-auto whitespace-nowrap font-light">
            {v.origine === 'leads' ? '📋 Leads' : '🤝 Reco'}
          </span>
        </div>
        <div className="text-xs text-ink-sub mt-1 truncate leading-relaxed">
          {v.prodLabel}{subProd && ` (${subProd})`} · {fmtK(v.montant)} · {v.agence}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-teal-dark font-demi whitespace-nowrap">{fmtEur(v.commission)}</div>
        <div className="text-[11px] text-ink-dim mt-1 font-light">{fmtDate(v.dateVente)}</div>
        <div className="flex gap-1 mt-1.5 justify-end">
          <Link
            to={`/declarer?edit=${v.id}`}
            className="text-xs text-srx hover:opacity-70"
            aria-label="Modifier la vente"
          >
            ✏️
          </Link>
          <button
            type="button"
            onClick={onDelete}
            className="text-xs text-ink-dim hover:text-red-500"
            aria-label="Supprimer la vente"
          >
            🗑
          </button>
        </div>
      </div>
    </li>
  );
}

function EmptyState({ hasSearch }: { hasSearch: boolean }) {
  if (hasSearch) {
    return (
      <div className="card text-center py-8">
        <div className="text-3xl mb-2">🔍</div>
        <div className="text-sm text-ink-sub">Aucun résultat pour cette recherche</div>
      </div>
    );
  }
  return (
    <div className="card text-center py-8">
      <div className="text-3xl mb-2">📋</div>
      <div className="text-sm text-ink-sub">Aucune vente sur cette période</div>
    </div>
  );
}

function pickIcon(prod: string): string {
  switch (prod) {
    case 'pu':           return '🛡️';
    case 'pp':           return '📅';
    case 'lodeom':       return '🌴';
    case 'scpi':         return '🏢';
    case 'immo-vente':   return '🏠';
    case 'immo-apport':  return '🤝';
    default:             return '💰';
  }
}
